import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  EditOutlined,
  AddPhotoAlternateOutlined,
  Close as CloseIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import type { IEquipment } from "../../types/equipment";
import apiService from "../../service/Api";
import Button from "../../components/common/Button";
import { tokens } from "../../theme";
import AddEquipmentDialog from "./components/AddEquipmentDialog";
import AnimatedTable, { ColumnDef } from "./components/AnimatedTable";
import { useNavigate } from "react-router-dom";
import { FormDataType } from "./types/types";

const ROWS_PER_PAGE = 10;

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

interface EquipmentApiItem {
  id: number | string;
  serial_number?: string;
  name?: string;
  brand?: string;
  category?: string;
  sub_category?: string;
  purchase_date?: string;
  status?: string;
  next_maintenance?: string;
  last_service_date?: string;
  hours_since_last_service?: number | string;
  assignee?: string;
  quantity?: string;
  location?: string;
  supplier?: string;
  last_used?: string;
  condition?: string;
  isKit?: boolean;
}

const ListGear: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<IEquipment | null>(null);
  const [filterCategory, setFilterCategory] = useState("All Categories");
  const [filterStatus, setFilterStatus] = useState("All Status");
  const [filterLocation, setFilterLocation] = useState("All Location");

  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [equipmentForImage, setEquipmentForImage] = useState<IEquipment | null>(
    null,
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [equipmentList, setEquipmentList] = useState<IEquipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const statusStyles: Record<string, { label: string; color: string }> = {
    Available: { label: "Available", color: colors.neutral[300] },
    Allocated: { label: "Allocated", color: colors.secondary[100] },
    Maintenance: { label: "Maintenance", color: colors.primary[400] },
    "Under Maintenance": { label: "Maintenance", color: colors.primary[400] },
    Inactive: { label: "Inactive", color: colors.neutral[400] },
    Retired: { label: "Retired", color: colors.grey[400] },
  };

  const fetchEquipment = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.get<{ results: EquipmentApiItem[] }>(
        "/api/equipments/equipment/",
      );
      const equipmentData: IEquipment[] = response.data.results.map((item) => {
        const hoursValue =
          typeof item.hours_since_last_service === "string"
            ? Number(item.hours_since_last_service)
            : item.hours_since_last_service;
        return {
          id: item.id.toString(),
          serialNo: item.serial_number || "",
          name: item.name || "",
          brand: item.brand || "",
          category: item.category || "",
          subCategory: item.sub_category || "",
          purchaseDate: item.purchase_date || "",
          status: item.status || "",
          nextMaintenance: item.next_maintenance || undefined,
          lastServiceDate: item.last_service_date || undefined,
          hoursSinceLastService:
            typeof hoursValue === "number" && !Number.isNaN(hoursValue)
              ? hoursValue
              : undefined,
          assignee: item.assignee || undefined,
          quantity: item.quantity || "",
          location: item.location || "",
          supplier: item.supplier || "",
          lastUsed: item.last_used || "",
          condition: item.condition || "",
          isKit: item.isKit || false,
        };
      });
      setEquipmentList(equipmentData);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to load equipment. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const handleEditEquipment = (equipment: IEquipment) => {
    setEditingEquipment(equipment);
    setAddDialogOpen(true);
  };

  const handleOpenImageUpload = (equipment: IEquipment) => {
    setEquipmentForImage(equipment);
    setSelectedFile(null);
    setImageUploadOpen(true);
  };

  const handleUploadImageSubmit = async () => {
    if (!selectedFile || !equipmentForImage) return;
    setIsUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("equipment", equipmentForImage.id.toString());

      await apiService.post("/api/equipments/equipment-images/", formData);

      setImageUploadOpen(false);
      setSelectedFile(null);
      setEquipmentForImage(null);
      await fetchEquipment();
    } catch (err: unknown) {
      const errorMessage =
        (err as ApiError)?.response?.data?.message || "Failed to upload image.";
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddEquipment = async (equipment: FormDataType) => {
    setError(null);
    try {
      const payload = {
        serial_number: equipment.serialNo,
        name: equipment.name,
        brand: equipment.brand,
        category: equipment.category,
        sub_category: equipment.subCategory,
        location: equipment.location,
        //purchase_date: equipment.purchaseDate,
        status: equipment.status,
        ...(equipment.purchaseDate && {
          purchase_date: equipment.purchaseDate,
        }),
      };


      if (editingEquipment) {
        await apiService.put(
          `/api/equipments/equipment/${editingEquipment.id}/`,
          payload,
        );
      } else {
        await apiService.post("/api/equipments/equipment/", payload);
      }

      setEditingEquipment(null);
      setAddDialogOpen(false);
      await fetchEquipment();
    } catch (err: unknown) {
      const errorMessage =
        (err as ApiError)?.response?.data?.message ||
        "Failed to save equipment. Please try again.";
      setError(errorMessage);
    }
  };

  const filteredEquipment = equipmentList.filter((eq) => {
    const matchesCategory =
      filterCategory === "All Categories" || eq.category === filterCategory;
    const matchesStatus =
      filterStatus === "All Status" || eq.status === filterStatus;
    const matchesLocation =
      filterLocation === "All Location" || eq.location === filterLocation;
    return matchesCategory && matchesStatus && matchesLocation;
  });

  const totalPages = Math.ceil(filteredEquipment.length / ROWS_PER_PAGE);
  const paginatedRows = filteredEquipment.slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE,
  );

  const categories = [
    "All Categories",
    ...Array.from(
      new Set(equipmentList.map((e) => e.category).filter(Boolean)),
    ),
  ];
  const statuses = [
    "All Status",
    ...Array.from(new Set(equipmentList.map((e) => e.status).filter(Boolean))),
  ];
  const locations = [
    "All Location",
    ...Array.from(
      new Set(equipmentList.map((e) => e.location || "").filter(Boolean)),
    ),
  ];

  const columns: ColumnDef<IEquipment>[] = [
    {
      key: "name",
      label: "Name",
      cellSx: { fontWeight: 500, color: theme.palette.text.primary },
      render: (eq) => (
        <Box display="flex" alignItems="center" gap={1}>
          {eq.name}
          {eq.isKit && (
            <Chip
              label="KIT"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                backgroundColor: colors.primary[900],
                color: theme.palette.text.secondary,
                fontWeight: "bold",
                borderRadius: "4px",
              }}
            />
          )}
        </Box>
      ),
    },
    { key: "category", label: "Category", render: (eq) => eq.category },
    {
      key: "status",
      label: "Status",
      render: (eq) => {
        const style = statusStyles[eq.status] ?? { label: eq.status };
        return (
          <Chip
            label={style.label}
            size="small"
            sx={{
              fontSize: 12,
              height: 26,
              backgroundColor: colors.neutral[700],
              color: theme.palette.text.primary,
              fontWeight: 500,
              borderRadius: "16px",
            }}
          />
        );
      },
    },
    { key: "quantity", label: "QTY", render: (eq) => eq.quantity || "—" },
    { key: "location", label: "Location", render: (eq) => eq.location || "—" },
    { key: "supplier", label: "Supplier", render: (eq) => eq.supplier || "—" },
    { key: "lastUsed", label: "Last Used", render: (eq) => eq.lastUsed || "—" },
    {
      key: "condition",
      label: "Condition",
      render: (eq) => eq.condition || "—",
    },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (eq) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          {/* New Upload Image Button */}
          <Tooltip title="Upload Photo" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenImageUpload(eq);
              }}
            >
              <AddPhotoAlternateOutlined
                sx={{ fontSize: 18, color: theme.palette.text.secondary }}
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditEquipment(eq);
              }}
            >
              <EditOutlined
                sx={{ fontSize: 18, color: theme.palette.text.secondary }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          color={theme.palette.text.primary}
          mb={0.5}
        >
          All Gear
        </Typography>
        <Typography variant="body2" color={theme.palette.text.primary} mb={3}>
          Inventory management
        </Typography>

        <Box
          sx={{
            backgroundColor: colors.primary[900],
            borderRadius: "16px",
            p: 4,
            minHeight: "70vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {[
                {
                  value: filterCategory,
                  setter: setFilterCategory,
                  options: categories,
                },
                {
                  value: filterStatus,
                  setter: setFilterStatus,
                  options: statuses,
                },
                {
                  value: filterLocation,
                  setter: setFilterLocation,
                  options: locations,
                },
              ].map(({ value, setter, options }) => (
                <FormControl key={value} size="small">
                  <Select
                    value={value}
                    onChange={(e) => {
                      setter(e.target.value);
                      setPage(0);
                    }}
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      height: 40,
                      borderRadius: "12px",
                      backgroundColor: colors.primary[900],
                      color: theme.palette.text.primary,
                      minWidth: 140,
                      "& .MuiSelect-icon": {
                        color: theme.palette.text.primary,
                      },
                    }}
                  >
                    {options.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Box>

            <Button
              variant="contained"
              onClick={() => setAddDialogOpen(true)}
              sx={{
                color: colors.primary[900],
                fontSize: 14,
                fontWeight: 500,
                px: 3,
                height: 40,
                borderRadius: "10px",
              }}
            >
              Add Gear
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
              <CircularProgress size={32} sx={{ color: colors.primary[400] }} />
            </Box>
          ) : (
            <AnimatedTable
              columns={columns}
              rows={paginatedRows}
              getRowId={(eq) => eq.id}
              onRowClick={(eq) => navigate(`/gear-management/view/${eq.id}`)}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              emptyMessage="No equipment found."
            />
          )}
        </Box>

        <AddEquipmentDialog
          open={addDialogOpen}
          onClose={() => {
            setAddDialogOpen(false);
            setEditingEquipment(null);
          }}
          onSubmit={handleAddEquipment}
          equipment={editingEquipment}
          isEdit={!!editingEquipment}
        />

        <Dialog
          open={imageUploadOpen}
          onClose={() => !isUploading && setImageUploadOpen(false)}
          PaperProps={{ sx: { borderRadius: 4, minWidth: 400 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Upload Photo
            </Typography>
            <IconButton
              onClick={() => setImageUploadOpen(false)}
              disabled={isUploading}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 4 }}
          >
            <Typography variant="body2" color="text.secondary">
              Attach a photo for <strong>{equipmentForImage?.name}</strong>.
            </Typography>

            <Button
              variant="outlined"
              component="label"
              sx={{
                py: 4,
                borderStyle: "dashed",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <AddPhotoAlternateOutlined
                sx={{ fontSize: 40, color: "text.secondary" }}
              />
              <Typography>
                {selectedFile ? selectedFile.name : "Click to select image"}
              </Typography>
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    setSelectedFile(e.target.files[0]);
                  }
                }}
              />
            </Button>

            <Button
              variant="contained"
              onClick={handleUploadImageSubmit}
              disabled={!selectedFile || isUploading}
              sx={{
                backgroundColor: colors.primary[400],
                borderRadius: "24px",
                py: 1.2,
                "&:hover": { backgroundColor: colors.primary[400] },
              }}
            >
              {isUploading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Upload Image"
              )}
            </Button>
          </DialogContent>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default ListGear;
